import type { PaginationInfo } from '../../components/Grids/shared/types';
import type { AsyncActionDefinition } from '../../components/Page/createController/types';

// The one params shape every list sends its route, whatever it lists.
export type ListParams = { search?: string; page?: number; sort?: string };

// A picker's server-searched options: the search they answer, the pages held so
// far, and whether the route has more. `scope` names whatever else narrows the
// list (one client's tasks), so a late answer for another scope is not shown.
export type OptionSearch<T> = {
  search: string;
  scope: string;
  items: T[];
  page: number;
  hasMore: boolean;
  loading: boolean;
};

// What a picker asks for: the source names the list, filters narrow it.
export type OptionQuery = {
  source: string;
  search: string;
  page: number;
  filters?: Record<string, string | boolean>;
};

export type OptionFetcher = (
  params: ListParams & { filters?: OptionQuery['filters'] },
) => Promise<{ items: unknown[]; pagination: PaginationInfo }>;

type OptionPage = { search: string; scope?: string; items: unknown[]; pagination: PaginationInfo };

export function emptyOptionSearch<T>(): OptionSearch<T> {
  return { search: '', scope: '', items: [], page: 0, hasMore: false, loading: false };
}

export function scopeOf(filters: OptionQuery['filters']): string {
  return filters ? JSON.stringify(filters) : '';
}

// The previous results stay on screen until the new ones land, so the list does
// not flash empty on every keystroke.
export function startOptionSearch<T>(
  state: OptionSearch<T>,
  { search, page, scope = '' }: { search: string; page: number; scope?: string },
): OptionSearch<T> {
  return {
    ...state,
    search,
    scope,
    loading: true,
    ...(page === 1 && { page: 0, hasMore: false }),
    ...(scope !== state.scope && { items: [] }),
  };
}

// Searches answer out of order; only the one still being typed gets to fill the
// list. A first page replaces it, a later one appends.
export function receiveOptionPage<T>(
  state: OptionSearch<T>,
  { search, scope = '', items, pagination }: OptionPage & { items: T[] },
): OptionSearch<T> {
  if (search !== state.search || scope !== state.scope) return state;
  return {
    search,
    scope,
    items: pagination.page === 1 ? items : [...state.items, ...items],
    page: pagination.page,
    hasMore: pagination.page < pagination.totalPages,
    loading: false,
  };
}

type WithOptions = { options: Record<string, OptionSearch<unknown>> };

// The one async action a page registers for every picker it shows: the source
// picks the route to ask, and the answer fills only that picker's slot.
export function optionQueryAction<TState extends WithOptions>(
  fetchers: Record<string, OptionFetcher>,
): AsyncActionDefinition<TState> {
  const slot = (state: TState, source: string) => state.options[source] ?? emptyOptionSearch();
  const withSlot = (state: TState, source: string, next: OptionSearch<unknown>): TState => ({
    ...state,
    options: { ...state.options, [source]: next },
  });

  return {
    action: async (input) => {
      const { source, search, page, filters } = input as OptionQuery;
      const { items, pagination } = await fetchers[source]({ search, page, filters });
      return { source, search, scope: scopeOf(filters), items, pagination };
    },
    onPending: (state, params) => {
      const query = params as OptionQuery;
      return withSlot(
        state,
        query.source,
        startOptionSearch(slot(state, query.source), { ...query, scope: scopeOf(query.filters) }),
      );
    },
    handlers: {
      onSuccess: (state, { action }) => {
        const answer = (action as { payload: OptionPage & { source: string } }).payload;
        return withSlot(
          state,
          answer.source,
          receiveOptionPage(slot(state, answer.source), answer),
        );
      },
      onError: (state, { params }) => {
        const { source } = params as OptionQuery;
        return withSlot(state, source, { ...slot(state, source), loading: false });
      },
    },
  };
}

// The props a SearchableSelect needs to search one source through the page's
// query action.
export function searchableSource(
  state: OptionSearch<unknown> | undefined,
  query: (params: { search: string; page: number }) => void,
) {
  const current = state ?? emptyOptionSearch();
  return {
    loading: current.loading,
    hasMore: current.hasMore,
    onSearch: (search: string) => query({ search, page: 1 }),
    onLoadMore: () => query({ search: current.search, page: current.page + 1 }),
  };
}
